import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/utils-app";
import ProfilePhotoField from "@/components/ProfilePhotoField";
import FaceSamplesField from "@/components/FaceSamplesField";
import ResponsivaDocumentField from "@/components/ResponsivaDocumentField";
import SubscriberAvatar from "@/components/SubscriberAvatar";
import { descriptorsFromDataUrls, loadFaceModels } from "@/lib/faceRecognition";
import { Plus, Pencil, Trash2, Search, X, Eye } from "lucide-react";

const todayDateInput = () => new Date().toISOString().slice(0, 10);

const empty = {
  full_name: "", birth_date: "", gender: "", phone: "", email: "",
  emergency_contact_name: "", emergency_contact_phone: "", profile_photo: null, responsible_photo: null,
  package_id: "", last_payment_date: "", enrollment_mode: "new", active: true, fitness_goal: "",
  username: "", password: "", password_confirm: "",
  face_samples: [], existing_samples_count: 0,
};

const toSubscriberPayload = (form, { isEdit, initialPhotos }) => {
  const payload = {
    full_name: form.full_name?.trim() || "",
    birth_date: form.birth_date || null,
    gender: form.gender || "",
    phone: form.phone || "",
    email: form.email || "",
    emergency_contact_name: form.emergency_contact_name || "",
    emergency_contact_phone: form.emergency_contact_phone || "",
    package_id: form.package_id || null,
    active: Boolean(form.active),
    fitness_goal: form.fitness_goal || "",
    username: (form.username || "").trim().toLowerCase(),
  };
  if (isEdit) {
    if (form.profile_photo !== initialPhotos?.profile) {
      payload.profile_photo = form.profile_photo || null;
    }
    if (form.responsible_photo !== initialPhotos?.responsible) {
      payload.responsible_photo = form.responsible_photo || null;
    }
  } else {
    payload.profile_photo = form.profile_photo || null;
    payload.responsible_photo = form.responsible_photo || null;
  }
  const pwd = (form.password || "").trim();
  if (isEdit) {
    if (pwd) payload.password = pwd;
  } else {
    payload.password = pwd;
    payload.enrollment_mode = form.enrollment_mode || "new";
    if (form.last_payment_date) {
      payload.last_payment_date = form.last_payment_date;
    }
  }
  return payload;
};

const subscriberToForm = (s) => ({
  ...empty,
  full_name: s.full_name || "",
  birth_date: s.birth_date || "",
  gender: s.gender || "",
  phone: s.phone || "",
  email: s.email || "",
  emergency_contact_name: s.emergency_contact_name || "",
  emergency_contact_phone: s.emergency_contact_phone || "",
  profile_photo: s.profile_photo || s.profile_photo_thumb || null,
  responsible_photo: s.responsible_photo || s.responsible_photo_thumb || null,
  package_id: s.package_id || "",
  active: s.active !== false,
  fitness_goal: s.fitness_goal || "",
  username: s.username || "",
  password: "",
  password_confirm: "",
  face_samples: [],
  existing_samples_count: Array.isArray(s.face_descriptors)
    ? s.face_descriptors.length
    : (s.face_descriptor ? 1 : 0),
});

export default function AdminSubscribers() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [initialPhotos, setInitialPhotos] = useState({ profile: null, responsible: null });

  const load = () => api.get("/admin/subscribers").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/packages").then((r) => setPackages(r.data));
    api.get("/admin/shifts").then((r) => setShifts(r.data || []));
  }, []);

  const selectedPackage = packages.find((p) => p.id === form.package_id);
  const packageShifts = useMemo(() => {
    const ids = selectedPackage?.shift_ids || [];
    if (!ids.length) return [];
    return shifts.filter((s) => ids.includes(s.id) && s.active !== false);
  }, [selectedPackage, shifts]);

  const startNew = () => {
    setForm(empty);
    setInitialPhotos({ profile: null, responsible: null });
    setEditing(null);
    setOpen(true);
    loadFaceModels().catch(() => {});
  };
  const startEdit = async (s) => {
    try {
      const { data } = await api.get(`/admin/subscribers/${s.id}`);
      setForm(subscriberToForm(data));
      setInitialPhotos({
        profile: data.profile_photo || null,
        responsible: data.responsible_photo || null,
      });
      setEditing(s.id);
      setOpen(true);
      loadFaceModels().catch(() => {});
    } catch {
      toast.error("No se pudo cargar el suscriptor");
    }
  };

  const isMigrated = !editing && form.enrollment_mode === "migrated";

  const save = async () => {
    const pwd = (form.password || "").trim();
    const pwdConfirm = (form.password_confirm || "").trim();
    if (!editing && !pwd) {
      toast.error("La contraseña es obligatoria para nuevos suscriptores");
      return;
    }
    if (pwd && pwd.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres");
      return;
    }
    if (pwd && pwd !== pwdConfirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    const payload = toSubscriberPayload(form, { isEdit: Boolean(editing), initialPhotos });
    const hasNewSamples = (form.face_samples || []).length > 0;
    const profileChanged = !editing || "profile_photo" in payload;
    if (hasNewSamples || profileChanged) {
      const sources = [form.profile_photo, ...(form.face_samples || [])].filter(Boolean);
      if (sources.length) {
        try {
          const descriptors = await descriptorsFromDataUrls(sources);
          if (descriptors.length) {
            payload.face_descriptors = descriptors;
            payload.face_descriptor = descriptors[0];
          } else {
            toast.warning("No se detectó rostro en las fotos; la asistencia facial no funcionará hasta una foto válida.");
          }
        } catch {
          toast.warning("No se pudo procesar el rostro de las fotos.");
        }
      }
    }
    if (!(payload.username || "").trim()) {
      toast.error("El usuario de acceso es obligatorio");
      return;
    }
    if (isMigrated) {
      if (!form.package_id) {
        toast.error("Indica el paquete del socio migrado");
        return;
      }
      if (!form.last_payment_date) {
        toast.error("Indica la fecha del último pago");
        return;
      }
    }
    try {
      if (editing) await api.put(`/admin/subscribers/${editing}`, payload);
      else await api.post("/admin/subscribers", payload);
      toast.success(editing && pwd ? "Suscriptor y contraseña actualizados" : "Suscriptor guardado");
      setOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error";
      toast.error(detail);
      if (!editing && String(detail).toLowerCase().includes("usuario")) {
        load();
      }
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar suscriptor y todo su historial?")) return;
    await api.delete(`/admin/subscribers/${id}`);
    toast.success("Eliminado");
    load();
  };

  const statusClass = (s) => ({
    valid: "badge-valid",
    expiring_soon: "badge-soon",
    overdue: "badge-overdue",
    unknown: "bg-white/5 text-white/40 border border-white/10",
  }[s] || "badge-status");

  const statusLabel = (s) => ({ valid: "Al día", expiring_soon: "Próximo a vencer", overdue: "Vencido", unknown: "Sin pagos" }[s] || s);

  const filtered = items.filter(i => {
    if (filter !== "all" && i.payment_status !== filter) return false;
    const name = (i.full_name || "").toLowerCase();
    const email = (i.email || "").toLowerCase();
    const query = q.toLowerCase();
    return name.includes(query) || email.includes(query);
  });

  return (
    <div data-testid="admin-subscribers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Atletas</div>
          <h1 className="font-display text-4xl text-white mt-1">Suscriptores</h1>
        </div>
        <button onClick={startNew} className="btn-primary text-sm" data-testid="new-subscriber-btn"><Plus size={14} /> Nuevo suscriptor</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
          <input className="input-dark pl-10" placeholder="Buscar por nombre o email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="subscribers-search" />
        </div>
        <select className="input-dark md:w-56" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">Todos los estados</option>
          <option value="valid">Al día</option>
          <option value="expiring_soon">Próximo a vencer</option>
          <option value="overdue">Vencido</option>
          <option value="unknown">Sin pagos</option>
        </select>
        {filter !== "all" && (
          <p className="text-xs text-white/40 w-full">
            Filtro activo: solo se muestran suscriptores con estado «{statusLabel(filter)}».
          </p>
        )}
      </div>

      <div className="mt-6 glass rounded-sm overflow-x-auto" data-testid="subscribers-table">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-theme-secondary/10">
              <th className="p-3">Atleta</th>
              <th className="p-3">Paquete</th>
              <th className="p-3">Último pago</th>
              <th className="p-3">Próxima fecha</th>
              <th className="p-3">Estado pago</th>
              <th className="p-3">Activo</th>
              <th className="p-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id} className="border-b border-theme-secondary/10 hover:bg-white/[0.02]" data-testid="subscriber-row">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <SubscriberAvatar subscriber={s} size={40} />
                    <div>
                      <div className="text-white font-medium">{s.full_name}</div>
                      <div className="text-xs text-white/40">{s.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-white/60">{s.package?.name || "—"}</td>
                <td className="p-3 text-white/60">{fmtDate(s.last_payment?.payment_date)}</td>
                <td className="p-3 text-white/60">{fmtDate(s.next_due_date)}</td>
                <td className="p-3"><span className={`badge-status ${statusClass(s.payment_status)}`}>{statusLabel(s.payment_status)}</span></td>
                <td className="p-3"><span className={`badge-status ${s.active ? "badge-valid" : "badge-overdue"}`}>{s.active ? "Sí" : "No"}</span></td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  <Link to={`/admin/suscriptores/${s.id}`} className="text-white/60 hover:text-[#FFD700] inline-flex"><Eye size={16} /></Link>
                  <button onClick={() => startEdit(s)} className="text-white/60 hover:text-[#FFD700]"><Pencil size={16} /></button>
                  <button onClick={() => del(s.id)} className="text-white/60 hover:text-red-400"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-white/40">No hay suscriptores</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="glass-strong max-w-2xl w-full rounded-sm my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-theme-secondary/10">
              <div className="font-display text-2xl text-white">{editing ? "Editar suscriptor" : "Nuevo suscriptor"}</div>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X /></button>
            </div>
            <div className="p-5 space-y-4">
              <ProfilePhotoField
                value={form.profile_photo}
                onChange={(profile_photo) => setForm((s) => ({ ...s, profile_photo }))}
                label="Foto del atleta"
                hint="Usada en asistencia facial y listados."
                cameraTitle="Tomar foto del atleta"
              />
              <FaceSamplesField
                value={form.face_samples}
                onChange={(face_samples) => setForm((s) => ({ ...s, face_samples }))}
                existingCount={form.existing_samples_count}
              />
              <ResponsivaDocumentField
                value={form.responsible_photo}
                onChange={(responsible_photo) => setForm((s) => ({ ...s, responsible_photo }))}
              />
              <div className="grid md:grid-cols-2 gap-4">
                {!editing && (
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase tracking-widest text-white/60">Tipo de alta</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[
                        { id: "new", label: "Nuevo (flujo normal)" },
                        { id: "migrated", label: "Ya inscrito (migración)" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => {
                            const migrated = opt.id === "migrated";
                            setForm((f) => ({
                              ...f,
                              enrollment_mode: opt.id,
                              active: migrated ? true : f.active,
                              last_payment_date: migrated
                                ? (f.last_payment_date || todayDateInput())
                                : f.last_payment_date,
                            }));
                          }}
                          className={`px-3 py-2 text-xs rounded-sm border ${
                            form.enrollment_mode === opt.id
                              ? "border-[#E63946] bg-[#E63946]/20 text-white"
                              : "border-white/20 text-white/60"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {isMigrated && (
                    <p className="text-[10px] text-white/40 mt-2">
                      Socio que ya pagaba en el gimnasio; se registrará activo y al día.
                      Usa la fecha real del último pago: si es antigua, aparecerá como vencido.
                    </p>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Nombre completo</label>
                  <input className="input-dark mt-1" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Fecha nacimiento</label>
                  <input type="date" className="input-dark mt-1" value={form.birth_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Género</label>
                  <select className="input-dark mt-1" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                    <option value="">—</option>
                    <option value="male">Masculino</option>
                    <option value="female">Femenino</option>
                    <option value="other">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Teléfono</label>
                  <input className="input-dark mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-white/60">Email</label>
                  <input type="email" className="input-dark mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Usuario de acceso</label>
                  <input className="input-dark mt-1" value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">{editing ? "Nueva contraseña" : "Contraseña"}</label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="input-dark mt-1"
                    placeholder={editing ? "Dejar vacío para no cambiar" : ""}
                    value={form.password || ""}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                  {editing && (
                    <p className="text-[10px] text-white/40 mt-1">Completa solo si quieres cambiar la contraseña del portal /mi</p>
                  )}
                </div>
                {(form.password || !editing) && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/60">
                      {editing ? "Confirmar nueva contraseña" : "Confirmar contraseña"}
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      className="input-dark mt-1"
                      value={form.password_confirm || ""}
                      onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Contacto emergencia (nombre)</label>
                  <input className="input-dark mt-1" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">Contacto emergencia (teléfono)</label>
                  <input className="input-dark mt-1" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60">
                    Paquete{isMigrated ? " *" : ""}
                  </label>
                  <select
                    className="input-dark mt-1"
                    value={form.package_id || ""}
                    onChange={(e) => setForm({ ...form, package_id: e.target.value })}
                  >
                    <option value="">—</option>
                    {packages.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                {form.package_id && packageShifts.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase tracking-widest text-white/60">Turnos incluidos en el paquete</label>
                    <p className="text-sm text-white/60 mt-1">
                      {packageShifts.map((s) => `${s.name} (${s.start_time}–${s.end_time})`).join(" · ")}
                    </p>
                    <p className="text-[10px] text-white/40 mt-1">
                      El suscriptor puede entrar en cualquiera de estos turnos según su horario.
                    </p>
                  </div>
                )}
                {!editing && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/60">
                      Fecha del último pago{isMigrated ? " *" : ""}
                    </label>
                    <input
                      type="date"
                      className="input-dark mt-1"
                      value={form.last_payment_date || ""}
                      onChange={(e) => setForm({ ...form, last_payment_date: e.target.value })}
                    />
                    <p className="text-[10px] text-white/40 mt-1">
                      {isMigrated
                        ? "Obligatoria. Base para calcular vencimiento y estado al día."
                        : "Opcional. Se usa para calcular la próxima fecha de pago y si está vencido."}
                    </p>
                  </div>
                )}
                {!form.profile_photo && (
                  <p className="md:col-span-2 text-xs text-amber-400/90">Toma foto de perfil con la cámara para reconocimiento facial en asistencia.</p>
                )}
                {!isMigrated && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/60">Estado</label>
                    <select className="input-dark mt-1" value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
                      <option value="1">Activo</option>
                      <option value="0">Inactivo</option>
                    </select>
                  </div>
                )}
                {isMigrated && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/60">Estado</label>
                    <p className="text-sm text-green-400/90 mt-2">Activo (migración)</p>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-white/60">Objetivo / notas</label>
                  <textarea rows={2} className="input-dark mt-1 resize-none" value={form.fitness_goal} onChange={(e) => setForm({ ...form, fitness_goal: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-theme-secondary/10 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="btn-outline text-sm py-2">Cancelar</button>
              <button onClick={save} className="btn-primary text-sm py-2" data-testid="save-subscriber">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
